import React from "react";

const IMG_BASE = "/static/cdss/user_manual/kinetikos-research/images";

const Image = ({ src, alt, className }) => (
  <img className={`user-manual__image ${className || ""}`} src={`${IMG_BASE}/${src}`} alt={alt} />
);

const Note = ({ children }) => (
  <p className="user-manual__note">
    <em>{children}</em>
  </p>
);

const StepBox = ({ title, children }) => (
  <div className="user-manual__stepbox">
    <div className="user-manual__stepbox-title">{title}</div>
    <ul>{children}</ul>
  </div>
);

const KinetikosResearchUserManual = () => {
  return (
    <div className="user-manual">
      <header className="user-manual__header">
        <h1 className="user-manual__study">KinetikOS Research</h1>
        <h2 className="user-manual__subtitle">User Manual</h2>
        <h3 className="user-manual__project">KinetikOS-Research</h3>
        <p className="user-manual__project-full">
          <em>Joint Action Cardiovascular Diseases and Diabetes</em>
        </p>
      </header>

      <section>
        <h2>1. Introduction</h2>
        <p>
          Welcome to the <strong>KinetikOS Research</strong> app, developed as part of the JACARDI
          (Joint Action Cardiovascular Diseases and Diabetes) project. This user manual guides you
          through all features of the application, helping you get the most out of your health
          tracking and research participation experience.
        </p>

        <h3>1.1 About This Manual</h3>
        <p>
          This manual covers every screen and feature available in the{" "}
          <strong>KinetikOS Research</strong> app. Each section describes the purpose of the
          feature, how to access it, and step-by-step instructions for use.
        </p>

        <h3>1.2 Who Is This App For?</h3>
        <p>
          The <strong>KinetikOS Research</strong> app is designed for research study participants
          enrolled in the JACARDI project. It enables you to:
        </p>
        <ul>
          <li>Log and track your daily medication schedule</li>
          <li>Record symptoms and daily events in a personal diary</li>
          <li>Monitor your physical activity and exercise routines</li>
          <li>Track your glucose levels via your Abbott LibreView device</li>
          <li>Access motivational content, nutritional tips, and exercise videos</li>
        </ul>

        <Note>
          This app is used within a research context. Data collected is shared with the JACARDI
          research platform for study purposes only. It is not intended to replace medical advice or
          clinical care.
        </Note>
      </section>

      <section>
        <h2>2. Getting Started</h2>

        <h3>2.1 Logging In</h3>
        <p>When you open the app, you will see the Login screen.</p>
        <div className="user-manual__figure">
          <Image src="image14.png" alt="Login screen" />
        </div>

        <StepBox title="Steps to Log In">
          <li>Enter your username in the Username field.</li>
          <li>Enter your password in the Password field.</li>
          <li>Tap SIGN IN to access the app.</li>
          <li>
            If your credentials are incorrect, an error message will appear. Check your username and
            password and try again.
          </li>
        </StepBox>

        <h3>2.2 Forgot Your Password?</h3>
        <ul>
          <li>On the Login screen, tap Forgot your password?</li>
          <li>Enter the email address linked to your account.</li>
          <li>Tap SEND RECOVERY. A recovery email will be sent to that address.</li>
          <li>Follow the instructions in the email to reset your password.</li>
        </ul>

        <h3>2.3 Forgot Your Username?</h3>
        <ul>
          <li>On the Login screen, tap Forgot your username?</li>
          <li>Enter the email address linked to your account.</li>
          <li>Tap SEND RECOVERY. Your username will be sent to that email address.</li>
        </ul>
        <div className="user-manual__figure user-manual__figure--row">
          <Image src="image17.png" alt="Forgot username screen" />
          <Image src="image15.png" alt="Forgot password screen" />
        </div>
      </section>

      <section>
        <h2>3. Home Screen</h2>
        <p>
          The Home screen is your main dashboard. It gives you an at-a-glance overview of your key
          health data and quick access to all app features.
        </p>

        <h3>3.1 What You Will See</h3>
        <div className="user-manual__figure">
          <Image src="image8.png" alt="Home screen" />
        </div>
        <ul>
          <li>A welcome message with your name</li>
          <li>Your current glucose level, plus the day's lowest and highest readings</li>
          <li>Your daily activity progress (steps toward your goal)</li>
          <li>Your exercise summary (movements completed vs. planned)</li>
          <li>A Recommendations section with tips and videos</li>
          <li>Quick-access cards for: Medication, Diary/Journal, My Care Team</li>
        </ul>

        <h3>3.2 Navigating from the Home Screen</h3>
        <p>Tap any card on the dashboard to navigate to that feature. For example:</p>
        <ul>
          <li>Tap the Glucose Level card to view your detailed Glucose Summary</li>
          <li>Tap the Activity card to open your Activity Summary</li>
          <li>Tap the Exercise card to view your Exercise Routine</li>
          <li>Tap Medication to manage your prescriptions and reminders</li>
          <li>Tap Diary Keeping to access your Event Journal</li>
          <li>Tap My Care Team to open the chat with your study team</li>
        </ul>

        <h3>3.3 Video Notifications</h3>
        <p>
          When a new exercise, nutritional, or motivational video is available, you will see a
          notification banner at the top of the Home screen. Tap View to watch the video directly.
        </p>
        <Note>
          Videos are made available on a weekly basis, following your enrollment date and
          participant profile. New content will appear automatically each week.
        </Note>
      </section>

      <section>
        <h2>4. Glucose Summary</h2>
        <p>
          The Glucose Summary screen displays your glucose data collected from your Abbott LibreView
          monitor. This data is collected for research purposes and is not used to provide medical
          guidance within the app.
        </p>

        <h3>4.1 Viewing Your Glucose Data</h3>
        <ul>
          <li>
            Navigate to the Glucose Summary by tapping the Glucose Level card on the Home screen.
          </li>
          <li>The screen displays a bar chart of your daily glucose average for the selected week.</li>
          <li>The week average and daily average are shown beneath the chart.</li>
          <li>For today, you will also see your Average, High, and Low glucose readings.</li>
        </ul>
        <div className="user-manual__figure">
          <Image src="image5.png" alt="Glucose summary screen" />
        </div>

        <h3>4.2 Changing the Time Range</h3>
        <ul>
          <li>
            Tap the week tabs at the top of the screen (e.g., 26-1 AUG, 2-8 AUG) to switch between
            different weekly periods.
          </li>
          <li>The chart and statistics will update automatically for the selected period.</li>
        </ul>
      </section>

      <section>
        <h2>5. Activity Summary</h2>
        <p>
          The Activity Summary screen shows your daily step count, distance, and progress toward
          your personal activity goal. Data is collected from your Samsung Galaxy SmartWatch.
        </p>

        <h3>5.1 Viewing Your Activity</h3>
        <ul>
          <li>Tap the Activity card on the Home screen to open the Activity Summary.</li>
          <li>
            A calendar at the top of the screen shows the current week. The selected date is
            highlighted.
          </li>
          <li>
            For the selected date, you will see: Steps taken, Distance travelled (km), and
            Percentage of your daily goal completed.
          </li>
          <li>
            A 24-hour bar chart shows your active minutes throughout the day, with a monthly average
            reference line.
          </li>
        </ul>

        <h3>5.2 Viewing Past Activity</h3>
        <ul>
          <li>Tap a different date on the calendar to view historical activity data.</li>
          <li>If no data is available for a selected date, the screen will indicate this.</li>
        </ul>
        <div className="user-manual__figure">
          <Image src="image18.png" alt="Activity summary screen" />
        </div>
      </section>

      <section>
        <h2>6. Exercise</h2>
        <p>
          The Exercise section lets you view and track your scheduled exercise routine. Routines are
          provided as part of your study programme.
        </p>

        <h3>6.1 Viewing Your Routine</h3>
        <ul>
          <li>Tap the Exercise card on the Home screen to open the Exercise Routine screen.</li>
          <li>A weekly calendar is displayed at the top. Tap a date to see that day's exercises.</li>
          <li>
            Each exercise entry shows: Name, Status (Pending, Complete, or Skipped), Sets, Reps, and
            Scheduled time.
          </li>
        </ul>

        <h3>6.2 Viewing Exercise Details</h3>
        <ul>
          <li>Tap any exercise item to open the Exercise Details screen.</li>
          <li>
            You will see a demonstration video or image, written instructions, sets, reps, and any
            safety notes.
          </li>
          <li>After completing the exercise, tap MARK AS FINISHED to update its status.</li>
        </ul>
        <div className="user-manual__figure user-manual__figure--row">
          <Image src="image11.png" alt="Exercise routine screen" />
          <Image src="image7.png" alt="Exercise details screen" />
          <Image src="image16.png" alt="Exercise completion screen" />
        </div>
      </section>

      <section>
        <h2>7. Medication</h2>
        <p>
          The Medication section allows you to log your personal medication schedule, set reminders,
          and record when you have taken or missed a dose. This information is recorded by you and
          is not updated by a clinician.
        </p>

        <h3>7.1 Medication Home</h3>
        <p>
          Access Medication by tapping the Medication card on the Home screen. From here you can:
        </p>
        <ul>
          <li>View your current medication list for the day</li>
          <li>Navigate to the Calendar view to see your schedule across months</li>
          <li>Tap Manage to add, edit, or delete medication entries</li>
          <li>Tap SOS to log an as-needed (SOS) medication</li>
        </ul>

        <h3>7.2 Logging a Medication as Taken, Not Taken, or Pending</h3>
        <ul>
          <li>On the Medication Home screen, tap a medication entry.</li>
          <li>A status panel will appear at the bottom of the screen.</li>
          <li>Tap TAKEN, NOT TAKEN, or PENDING to record your status.</li>
          <li>The status will be saved and the screen will return to the medication list.</li>
        </ul>
        <div className="user-manual__figure user-manual__figure--row">
          <Image src="image3.png" alt="Medication home screen" />
          <Image src="image10.png" alt="Medication status panel" />
        </div>

        <h3>7.3 Adding a New Medication</h3>
        <ul>
          <li>Tap Manage from the Medication Home screen.</li>
          <li>Tap Add New Medication.</li>
          <li>Enter the medication Name (required).</li>
          <li>Optionally set an End Date for the medication course.</li>
          <li>
            Toggle SOS Medication ON if this is an as-needed medicine (no fixed schedule).
          </li>
          <li>
            Set the scheduled Time for regular medications. Tap Add another time to add multiple
            daily doses.
          </li>
          <li>Set the repeat frequency (e.g., daily, weekly).</li>
          <li>Add any Notes if needed.</li>
          <li>Tap Add Medication to save.</li>
        </ul>

        <h3>7.4 Logging an SOS Medication</h3>
        <ul>
          <li>Tap SOS from the Medication Home screen.</li>
          <li>Select the SOS medication you have taken from the list.</li>
          <li>Confirm or adjust the Date and Time.</li>
          <li>Tap Add Medication to save the record.</li>
        </ul>

        <h3>7.5 Medication Calendar</h3>
        <p>
          Tap Calendar from the Medication Home screen to see a full monthly calendar view of your
          medication schedule. Tap any date to view that day's medications. Navigate between months
          by scrolling up or down.
        </p>
        <div className="user-manual__figure user-manual__figure--row">
          <Image src="image9.png" alt="Medication calendar month view" />
          <Image src="image4.png" alt="Medication calendar day view" />
        </div>
      </section>

      <section>
        <h2>8. Event Journal (Diary)</h2>
        <p>
          The Event Journal allows you to record daily events and symptoms for your personal
          reference and as part of the research data collection.
        </p>

        <h3>8.1 Accessing the Journal</h3>
        <p>
          Tap Diary Keeping on the Home screen. A horizontal weekly calendar is displayed. The
          current date is highlighted. Swipe left or right to navigate between weeks. Tap the
          calendar icon for a full monthly view.
        </p>

        <h3>8.2 Adding a New Entry</h3>
        <ul>
          <li>Tap the + button at the top right of the Journal screen.</li>
          <li>The New Event screen will open.</li>
          <li>
            Select the symptoms that occurred during the event by tapping them from the list, or use
            the Search bar to find a specific symptom.
          </li>
          <li>If your symptom is not listed, tap Add a new one and type it in.</li>
          <li>Tap ADD TIMING to proceed.</li>
          <li>On the timing screen, confirm or adjust the Date and Time of the event.</li>
          <li>Optionally add any Additional Info in the free-text field.</li>
          <li>Tap UPDATE EVENT to save your entry.</li>
        </ul>

        <h3>8.3 Editing an Existing Entry</h3>
        <ul>
          <li>Navigate to the date of the entry in the calendar.</li>
          <li>Tap the entry you wish to edit.</li>
          <li>Adjust symptoms, date, time, or additional info as needed.</li>
          <li>Tap UPDATE EVENT to save your changes.</li>
        </ul>
        <div className="user-manual__figure user-manual__figure--row">
          <Image src="image13.png" alt="Event journal screen" />
          <Image src="image2.png" alt="New event screen" />
        </div>
      </section>

      <section>
        <h2>9. Recommendations</h2>
        <p>
          The Recommendations screen provides personalised health and wellness content including
          nutritional tips, motivational guidance, and supporting literature.
        </p>

        <h3>9.1 Viewing Recommendations</h3>
        <ul>
          <li>Tap Recommendations from the Home screen.</li>
          <li>
            A list of recommendation cards is displayed. Each card shows a Title, Type (e.g.,
            Nutritional, Motivational), and a short description or video.
          </li>
          <li>Tap a card to read the full recommendation or watch the associated video.</li>
        </ul>
        <Note>
          Recommendations are based on your study programme. New content may appear throughout the
          study period.
        </Note>
        <div className="user-manual__figure">
          <Image src="image1.png" alt="Recommendations screen" />
        </div>
      </section>

      <section>
        <h2>10. My Care Team (Chat)</h2>
        <p>
          The My Care Team section allows you to send and receive messages with your study
          coordinator or research team.
        </p>
        <div className="user-manual__figure">
          <Image src="image6.png" alt="My Care Team chat screen" />
        </div>

        <h3>10.1 Opening a Conversation</h3>
        <ul>
          <li>Tap My Care Team from the Home screen.</li>
          <li>A list of available conversations is shown.</li>
          <li>Tap a conversation to open the chat window.</li>
        </ul>

        <h3>10.2 Sending a Message</h3>
        <ul>
          <li>Type your message in the text field at the bottom of the screen.</li>
          <li>Tap the send button (paper aeroplane icon) to send.</li>
          <li>Your message will appear in the conversation history.</li>
        </ul>

        <h3>10.3 Receiving Messages</h3>
        <p>
          Messages from your study team will appear in the conversation history, labelled with the
          sender name and timestamp. The chat will automatically scroll to show the most recent
          message.
        </p>
        <Note>This chat is intended for study-related communication only.</Note>
      </section>

      <section>
        <h2>11. Connected Devices and Data Sources</h2>

        <h3>11.1 Abbott LibreView (Glucose Monitor)</h3>
        <p>
          Your glucose data is automatically collected from your Abbott Libre sensor via the
          LibreView platform. Ensure your Abbott device is set up and your LibreView account is
          linked to the app. If data is not appearing, check your device connection and internet
          access.
        </p>

        <h3>11.2 Samsung Galaxy SmartWatch 7 (Activity Data)</h3>
        <p>
          Activity and exercise data is collected from your Samsung Galaxy SmartWatch 7 via Samsung
          Health Connect. Ensure your smartwatch is paired and Health Connect is authorised. Sync
          your watch regularly to keep data up to date.
        </p>
        <div className="user-manual__figure">
          <Image src="image12.png" alt="Connected devices screen" />
        </div>
      </section>

      <section>
        <h2>12. Privacy and Data</h2>
        <p>
          Your privacy is important. The <strong>KinetikOS Research</strong> app is designed in
          compliance with strict European privacy laws (GDPR).
        </p>
        <ul>
          <li>You control which data is shared and with which institution or research platform.</li>
          <li>You can revoke data sharing permissions at any time from your account settings.</li>
          <li>Data collected through this app is used solely for the JACARDI research study.</li>
          <li>Personal health data is never sold or shared with third parties outside the study.</li>
        </ul>
        <Note>
          For full details on how your data is used, refer to the Privacy Policy available on the
          Login screen.
        </Note>
      </section>

      <section>
        <h2>13. Support</h2>
        <p>
          If you experience any issues not covered by this manual, or have questions about the
          study, please contact the JACARDI study team via the My Care Team chat within the app, or
          reach out through the contact details provided to you at enrolment.
        </p>
      </section>

      <footer className="user-manual__footer">
        <span>KinetikOS Research &mdash; JACARDI User Manual</span>
      </footer>
    </div>
  );
};

export default React.memo(KinetikosResearchUserManual);
